import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Mixpanel from '../utils/mixpanel';
import styles from './Home.module.css';

const slides = [
  {
    id: 'intro',
    title: "MENTAL TRAINING FOR ATHLETES",
    tagline: "ELEVATE YOUR GAME",
    description: "Train your mind like you train your body to unlock your full performance potential.",
    bg: "bg-gradient-to-br from-blue-100 to-indigo-200",
    logo: true,
    ctaText: "Discover How"
  },
  {
    id: 'confidence',
    title: "BUILD LASTING CONFIDENCE",
    tagline: "PRACTICE MAKES PERMANENT",
    subheading: "Confidence isn't just a feeling — it's a skill earned through consistent practice.",
    description: "You build it through action. Through repetition. Through ownership of your journey.",
    keyPoints: [
      { icon: "🎯", text: "Reconnect with your Goals and Motivation" },
      { icon: "💪", text: "Develop a strong and resilient Mindset" },
      { icon: "⚙️", text: "Set and follow through on Process Goals" },
      { icon: "🧘", text: "Build focus with Breathwork and Concentration" },
      { icon: "📝", text: "Reflect on Wins & Gratitude with Performance Journals" }
    ],
    finalHeadline: "Confidence is not given. It's practiced. Let's go get it.",
    bg: "bg-gradient-to-br from-indigo-100 to-purple-200",
    ctaText: "Learn More"
  },
  {
    id: 'personalized',
    title: "PERSONALIZED AI COACHING",
    tagline: "JUST FOR YOU",
    description: "Mental Edge Coach tailors your training based on your age, sport, position, and goals — in just 15 minutes a day.",
    keyFeatures: [
      { title: "AI-Powered Personalization", description: "Training tailored to your specific needs" },
      { title: "Just 15 Minutes a Day", description: "1% of your time for exponential mental growth" },
      { title: "Compound Improvement", description: "1% better each day = 37x better in one year" }
    ],
    bg: "bg-gradient-to-br from-purple-100 to-indigo-200",
    ctaText: "Start Free Training"
  },
];

const Home = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  
  useEffect(() => {
    Mixpanel.track('View Home Page');
  }, []);
  
  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    } else {
      navigate('/new-profile');
    }
  };

  const handleDotClick = (index) => {
    setCurrentSlide(index);
  };

  return (
    <div className={styles.homeContainer}>
      {/* Current slide */}
      {(() => {
        const slide = slides[currentSlide];
        return (
          <div 
            key={currentSlide} 
            className={`w-full h-full flex flex-col ${slide.bg}`}
          >
            {/* Background pattern overlay */}
            <div className={styles.bgPattern}></div>
            
            {/* Main content container */}
            <div className={styles.contentContainer}>
                {/* Logo (only on first slide) */}
                {slide.logo && (
                  <div className={styles.logoContainer}>
                    <img
                      src="/images/mental-edge-coach-logo.png"
                      alt="Mental Edge Coach"
                      className="w-28 md:w-32 mb-4"
                      onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
                    />
                  </div>
                )}
                
                {/* Tagline */}
                {slide.tagline && (
                  <div className={styles.taglineContainer}>
                    <span className={styles.tagline}>{slide.tagline}</span>
                  </div>
                )}
                
                {/* Title */}
                <h1 className={styles.mainTitle}>
                  {slide.icon && <span className={styles.titleIcon}>{slide.icon} </span>}
                  {slide.title}
                </h1>
                
                {/* Subheading */}
                {slide.subheading && (
                  <h2 className={styles.subheading}>{slide.subheading}</h2>
                )}
                
                {/* Description */}
                {slide.description && (
                  <p className={styles.description}>{slide.description}</p>
                )}
                

                
                {/* Navigation Controls - conditionally positioned based on slide */}
                {currentSlide === 0 && (
                  <div className={styles.navigationControls}>
                    {/* CTA Button */}
                    <button 
                      onClick={handleNext}
                      className={styles.ctaButton}
                    >
                      {slide.ctaText}
                      <span className={styles.buttonArrow}>→</span>
                    </button>
                    
                    {/* Login Link */}
                    <div className={styles.loginContainer}>
                      <span>Already a member?</span>
                      <Link to="/login" className={styles.loginLink}>Log in</Link>
                    </div>
                  </div>
                )}
                
                {/* Key Points */}
                {slide.keyPoints && (
                  <ul className={styles.keyPointsList}>
                    {slide.keyPoints.map((point, idx) => (
                      <li key={idx} className={styles.keyPoint}>
                        <span className={styles.keyPointIcon}>{point.icon}</span>
                        <span className={styles.keyPointText}>{point.text}</span>
                      </li>
                    ))}
                  </ul>
                )}
                
                {/* Key Features */}
                {slide.keyFeatures && (
                  <div className={styles.keyFeatures}>
                    {slide.keyFeatures.map((feature, idx) => (
                      <div key={idx} className={styles.feature}>
                        <h3 className={styles.featureTitle}>{feature.title}</h3>
                        <p className={styles.featureDescription}>{feature.description}</p>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Navigation Controls for slide 3 - positioned after key features */}
                {currentSlide === 2 && slide.keyFeatures && (
                  <div className={styles.navigationControls}>
                    {/* CTA Button */}
                    <button 
                      onClick={handleNext}
                      className={styles.ctaButton}
                    >
                      {'START TRAINING NOW'}
                      <span className={styles.buttonArrow}>→</span>
                    </button>
                    
                    {/* Login Link */}
                    <div className={styles.loginContainer}>
                      <span>Already a member?</span>
                      <Link to="/login" className={styles.loginLink}>Log in</Link>
                    </div>
                  </div>
                )}
                

                
                {/* Final Headline */}
                {slide.finalHeadline && (
                  <p className={styles.finalHeadline}>{slide.finalHeadline}</p>
                )}
                
                {/* Navigation Controls for slide 1 */}
                {currentSlide === 1 && (
                  <div className={styles.navigationControls}>
                    {/* CTA Button */}
                    <button 
                      onClick={handleNext}
                      className={styles.ctaButton}
                    >
                      {slide.ctaText}
                      <span className={styles.buttonArrow}>→</span>
                    </button>
                    
                    {/* Login Link */}
                    <div className={styles.loginContainer}>
                      <span>Already a member?</span>
                      <Link to="/login" className={styles.loginLink}>Log in</Link>
                    </div>
                  </div>
                )}
                
                {/* Progress indicator - moved up to make room for privacy links */}
                <div className={styles.progressContainer} style={{ marginBottom: '1rem' }}>
                  {slides.map((_, idx) => (
                    <button
                      key={idx}
                      className={`${styles.progressDot} ${idx === currentSlide ? styles.activeDot : ''}`}
                      onClick={() => handleDotClick(idx)}
                      aria-label={`Go to slide ${idx + 1}`}
                    />
                  ))}
                </div>

                {/* Privacy and Terms Links */}
                <div className="text-center mt-4">
                  <span className="text-xs text-gray-600">
                    <Link to="/terms-and-conditions" className="text-blue-500 hover:text-blue-700" target="_blank" rel="noopener noreferrer">Terms & Conditions</Link>
                    {' • '}
                    <Link to="/privacy-policy" className="text-blue-500 hover:text-blue-700" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>
                  </span>
                </div>
              </div>
            </div>
          );
        })()}
    </div>
  );
};

export default Home;
